import { motion, AnimatePresence } from "framer-motion";
import { useState, type FormEvent } from "react";
import { submitLead, type LeadPayload } from "../lib/api";
import { SectionHeading } from "../components/SectionHeading";
import { PixelButton } from "../components/PixelButton";

const PROJECT_TYPES = [
  "Marketing site",
  "Web app",
  "Rebuild / migration",
  "Brand + site",
  "Something else",
];

const BUDGETS = [
  "< $5k",
  "$5k–$15k",
  "$15k–$40k",
  "$40k–$100k",
  "$100k+",
  "Not sure yet",
];

const TIMELINES = [
  "ASAP / yesterday",
  "Within 1 month",
  "1–3 months",
  "3+ months",
  "Flexible",
];

type Status = "idle" | "submitting" | "success" | "error";

const initial: LeadPayload = {
  name: "",
  email: "",
  company: "",
  project_type: PROJECT_TYPES[0],
  budget: BUDGETS[0],
  timeline: TIMELINES[1],
  message: "",
  website: "",
};

export function Contact() {
  const [data, setData] = useState<LeadPayload>(initial);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof LeadPayload>(key: K, value: LeadPayload[K]) =>
    setData((d) => ({ ...d, [key]: value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;

    if (!data.name.trim() || !data.email.trim() || !data.message.trim()) {
      setError("Name, email, and a message are required.");
      setStatus("error");
      return;
    }
    setError(null);
    setStatus("submitting");
    try {
      await submitLead({ ...data, source: "cauldrondevelopment.io" });
      setStatus("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
      setStatus("error");
    }
  }

  return (
    <section id="contact" className="relative py-24 md:py-32 px-4 md:px-8">
      <div className="mx-auto max-w-[1100px]">
        <SectionHeading
          kicker="// CONTACT"
          title={
            <>
              Get in touch.{" "}
              <span className="shimmer-text">We reply to everyone.</span>
            </>
          }
          blurb="Tell us about your project. We respond within two business days. No bots, no auto-replies."
        />

        <div className="bevel-out bg-grimoire p-1">
          <div className="relative noise bg-gradient-to-br from-grimoire via-bruise to-grimoire p-6 md:p-10 border-2 border-black">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <SuccessState key="success" onReset={() => { setData(initial); setStatus("idle"); }} />
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -10 }}
                  onSubmit={onSubmit}
                  className="space-y-6"
                  noValidate
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <Field
                      label="Your name"
                      required
                      value={data.name}
                      onChange={(v) => update("name", v)}
                    />
                    <Field
                      label="Email"
                      type="email"
                      required
                      value={data.email}
                      onChange={(v) => update("email", v)}
                    />
                    <Field
                      label="Company / project name"
                      value={data.company ?? ""}
                      onChange={(v) => update("company", v)}
                    />
                    <Select
                      label="Project type"
                      value={data.project_type}
                      onChange={(v) => update("project_type", v)}
                      options={PROJECT_TYPES}
                    />
                    <Select
                      label="Budget"
                      value={data.budget}
                      onChange={(v) => update("budget", v)}
                      options={BUDGETS}
                    />
                    <Select
                      label="Timeline"
                      value={data.timeline ?? ""}
                      onChange={(v) => update("timeline", v)}
                      options={TIMELINES}
                    />
                  </div>

                  <Textarea
                    label="About the project"
                    required
                    value={data.message}
                    onChange={(v) => update("message", v)}
                    placeholder="Audience, goal, vibe, what success looks like, any links to inspiration…"
                  />

                  <div className="hidden" aria-hidden>
                    <label>
                      Website
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={data.website ?? ""}
                        onChange={(e) => update("website", e.target.value)}
                      />
                    </label>
                  </div>

                  {error && (
                    <div
                      role="alert"
                      className="bevel-in bg-void p-3 font-terminal text-sm text-ember"
                    >
                      ⚠ {error}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                    <p className="font-terminal text-xs text-mystic max-w-md">
                      // your info goes only to us. no list. no newsletter. no resale.
                    </p>
                    <PixelButton
                      type="submit"
                      variant="acid"
                      size="lg"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Sending…" : "Send →"}
                    </PixelButton>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block font-terminal text-mystic text-xs uppercase tracking-widest mb-1">
        {label} {required && <span className="text-acid">*</span>}
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bevel-in bg-void px-3 py-3 font-pixel text-base text-bone placeholder:text-aura/30 focus:outline-none focus:ring-2 focus:ring-witch"
      />
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <label className="block">
      <span className="block font-terminal text-mystic text-xs uppercase tracking-widest mb-1">
        {label}
      </span>
      <div className="bevel-in bg-void">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 py-3 font-pixel text-base text-bone focus:outline-none pointer"
        >
          {options.map((o) => (
            <option key={o} value={o} className="bg-night text-bone">
              {o}
            </option>
          ))}
        </select>
      </div>
    </label>
  );
}

function Textarea({
  label,
  value,
  onChange,
  required = false,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="block font-terminal text-mystic text-xs uppercase tracking-widest mb-1">
        {label} {required && <span className="text-acid">*</span>}
      </span>
      <textarea
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        placeholder={placeholder}
        className="w-full bevel-in bg-void px-3 py-2 font-pixel text-base text-bone placeholder:text-aura/30 resize-y focus:outline-none focus:ring-2 focus:ring-witch"
      />
    </label>
  );
}

function SuccessState({ onReset }: { onReset: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="text-center py-12"
    >
      <motion.img
        src="/cauld.png"
        alt=""
        className="pixelated mx-auto w-32 h-32 mb-6 drop-shadow-[0_0_24px_rgba(155,61,245,0.9)]"
        animate={{ rotate: [-3, 3, -3] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
      />
      <h3 className="font-display text-2xl md:text-3xl text-bone leading-tight">
        Got it.
      </h3>
      <p className="mt-3 font-pixel text-lg text-aura/85 max-w-lg mx-auto">
        Thanks — we'll be in touch within{" "}
        <span className="text-acid">two business days</span>.
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-6 font-terminal text-sm text-mystic underline decoration-dotted underline-offset-4 hover:text-bone pointer"
      >
        Send another →
      </button>
    </motion.div>
  );
}
